import { getCalendarRange } from '@src/packages/utils/dates/calendar';
import React, { useMemo } from 'react';

import { useHistory, useParams, useRouteMatch } from 'react-router-dom';

import { Drawer } from 'antd';
import { auth, db } from 'firebase/firebase';
import { collection, query, where } from 'firebase/firestore';
import { PropTypes } from 'prop-types';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import EventTypeList from 'components/Events/EventTypeList';
import FilterContainer from 'components/FilterContainer';

import { getRouteToTimelineEventDrawer } from 'packages/dashboard/constants';
import { calculateScheduleProgress } from 'packages/dashboard/screens/SchedulesDashboard/constants';
import { camelize, getFullUrl, useFirestoreQuery } from 'packages/utils';
import useCloudFunction from 'packages/utils/hooks/clouds/useCloudFunction';
import useGetParams from 'packages/utils/hooks/useGetParams';

import { FSCollections } from '@src/constants/database';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';

import styles from './styles.module.less';

export default function EventTypesDrawer({ isVisible = false, onCancel = null }) {
  const history = useHistory();
  const { url } = useRouteMatch();
  const q = useGetParams();
  const { orgId } = useParams();

  const divId = q.get('divId');
  const gte = q.get('gte');
  const lte = q.get('lte');
  const interval = q.get('interval');
  const scheduleId = q.get('scheduleId');
  const dateKey = q.get('dateKey');
  const groupBy = q.get('groupBy')?.replace('/event_types', '');
  const calendarTimeZone = q.get('calendarTimeZone');

  const paramsSent = {
    organizationId: orgId,
    divisionIds: [divId],
    uid: auth.currentUser.uid,
    range: {
      gte,
      lte,
    },
    interval,
    ...(calendarTimeZone ? { calendarRange: getCalendarRange({ gte, lte }, calendarTimeZone) } : {}),
    scheduleIds: [scheduleId],
  };

  const {
    data: scheduleFetched,
    loading,
    error,
  } = useCloudFunction({
    params: paramsSent,
    dependencies: [isVisible],
    op: API_OPERATIONS.SCHEDULE_DASHBOARD,
    parseData: result => {
      const output = camelize(result);
      return output.aggregations.timeRange.histogram.buckets;
    },
  });

  const divEventTypesRef = query(
    collection(
      db,
      FSCollections.ORGANIZATIONS,
      orgId,
      FSCollections.DIVISIONS,
      divId,
      FSCollections.SCHEDULES,
    ),
    where('id', '==', scheduleId),
  );
  const { data: scheduleData = [], loading: scheduleLoading } = useFirestoreQuery(
    divEventTypesRef,
    [orgId, divId, scheduleId],
  );

  const scheduleNameTitle = scheduleData?.[0]?.name || '';

  const triggersById = useMemo(
    () =>
      scheduleData?.[0]?.triggers?.reduce((prevValue, trigger) => {
        const copy = { ...prevValue };

        copy[trigger.id] = {
          trigger,
        };
        return copy;
      }, {}) || {},
    [scheduleData],
  );

  const eventTypeListData = useMemo(() => {
    if (Object.keys(triggersById).length === 0 || scheduleFetched.length === 0) return [];

    const scheduleByDateKey = scheduleFetched.filter(sch => Number(sch.key) === Number(dateKey));
    const schData = scheduleByDateKey?.[0]?.bySchedule?.buckets[0];
    const scheduleProgress = schData && calculateScheduleProgress(schData);

    if (!scheduleProgress) return [];

    return scheduleProgress.triggers?.map(trigger => {
      const triggerBySch = triggersById[trigger.key];
      return {
        ...triggerBySch?.trigger,
        finishedExecutions: trigger.finishedTriggerExecutions?.value,
        expectedExecutions: trigger.expectedTriggerExecutions?.value,
        finishedPercentage: trigger.triggerFinishedPercentage?.value,
      };
    }) ?? [];
  }, [dateKey, scheduleFetched, triggersById]);

  const mainLoading = loading || scheduleLoading;

  const goToTimeline = trId => {
    const path = getFullUrl(
      getRouteToTimelineEventDrawer(
        orgId,
        divId,
        trId,
        scheduleId,
        paramsSent.range.gte,
        paramsSent.range.lte,
        dateKey,
        groupBy,
      ),
      url,
    );

    history.push(calendarTimeZone ? `${path}&calendarTimeZone=${encodeURIComponent(calendarTimeZone)}` : path, {
      shadow: true,
    });
  };

  return (
    <Drawer
      className={styles.drawer}
      closable={false}
      destroyOnClose
      footer={null}
      open={isVisible}
      title={null}
      onClose={onCancel}
    >
      <BoxContainer className={styles.layout} content bottomBorder fixed>
        <FilterContainer title={<Title.Header value={scheduleNameTitle} />} goBack={onCancel} />
      </BoxContainer>
      <BoxContainer
        className={styles.body}
        content
        empty={!mainLoading && eventTypeListData.length === 0}
        loading={mainLoading}
        error={error}
      >
        <EventTypeList.SimpleList
          data={eventTypeListData}
          infoCardType="primary"
          onGoToTimeline={triggerId => goToTimeline(triggerId)}
        />
      </BoxContainer>
    </Drawer>
  );
}

EventTypesDrawer.propTypes = {
  isVisible: PropTypes.bool,
  onCancel: PropTypes.func,
};
